import QueryString from 'query-string';

import { toastr } from 'react-redux-toastr';

import { push } from 'react-router-redux';

import {
  generateManualVoucherPaymentReconciliationEndpoint,
  MANUAL_VOUCHER_PAYMENT_ENDPOINT,
  MANUAL_VOUCHER_PAYMENT_RECONCILIATION_ENDPOINT,
} from '../../config/endpoints';

import {
  DEFAULT_DELETE_CONFIG,
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
} from '../../config/rest';

import { isErrorResponse } from '../../utils/error-handlers';

import { MANUAL_VOUCHER_PAYMENT_PATH } from '../../config/paths';
import {
  CLEAR_MANUAL_VOUCHER_PAYMENT,
  CLEAR_MANUAL_VOUCHER_PAYMENT_RECONCILIATION,
  CLEAR_MANUAL_VOUCHER_PAYMENTS,
  FLAG_GETTING_MANUAL_VOUCHER_PAYMENTS,
  FLAG_MANUAL_VOUCHER_PAYMENT_ACTIVITY,
  FLAG_MANUAL_VOUCHER_PAYMENT_RECONCILIATION,
  GET_MANUAL_VOUCHER_PAYMENT,
  GET_MANUAL_VOUCHER_PAYMENT_RECONCILIATION,
  GET_MANUAL_VOUCHER_PAYMENTS,
} from '../types/corporate/ManualVoucherPayment';

const flagGettingManualVoucherPayments = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_MANUAL_VOUCHER_PAYMENTS,
    payload: flag,
  });

const flagManualVoucherPaymentActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_MANUAL_VOUCHER_PAYMENT_ACTIVITY,
    payload: flag,
  });

const getManualVoucherPayments = async (tableFilters) => async (dispatch) => {
  try {
    dispatch(flagGettingManualVoucherPayments(true));

    const url = `${MANUAL_VOUCHER_PAYMENT_ENDPOINT}?${QueryString.stringify(
      tableFilters,
    )}`;

    const response = await fetch(url, DEFAULT_GET_CONFIG);

    await isErrorResponse(response, null, dispatch);

    const manualVoucherPayments = await response.json();

    dispatch({
      type: GET_MANUAL_VOUCHER_PAYMENTS,
      payload: manualVoucherPayments,
    });
  } catch (error) {
    toastr.error('Error', error.message);
  } finally {
    dispatch(flagGettingManualVoucherPayments(false));
  }
};

const clearManualVoucherPayments = () => (dispatch) =>
  dispatch({
    type: CLEAR_MANUAL_VOUCHER_PAYMENTS,
  });

const getManualVoucherPayment =
  async ({ manualVoucherPaymentId }) =>
  async (dispatch) => {
    try {
      dispatch(flagManualVoucherPaymentActivity(true));

      const url = `${MANUAL_VOUCHER_PAYMENT_ENDPOINT}/${manualVoucherPaymentId}`;

      const response = await fetch(url, DEFAULT_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const manualVoucherPayment = await response.json();

      dispatch({
        type: GET_MANUAL_VOUCHER_PAYMENT,
        payload: manualVoucherPayment,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagManualVoucherPaymentActivity(false));
    }
  };

const clearManualVoucherPayment = () => (dispatch) =>
  dispatch({
    type: CLEAR_MANUAL_VOUCHER_PAYMENT,
  });

const postManualVoucherPayment = async (payments) => async (dispatch) => {
  try {
    dispatch(flagManualVoucherPaymentActivity(true));

    const response = await fetch(MANUAL_VOUCHER_PAYMENT_ENDPOINT, {
      ...DEFAULT_POST_CONFIG,
      body: JSON.stringify(payments),
    });

    await isErrorResponse(response, null, dispatch);

    const manualVoucherPayments = await response.json();

    const approvedCount = manualVoucherPayments.filter(
      ({ approved }) => approved,
    ).length;

    toastr.success(
      'Éxito',
      approvedCount
        ? `Pagos registrados correctamente. Aprobados automáticamente: ${approvedCount}`
        : 'Pagos registrados correctamente',
    );

    dispatch(push(MANUAL_VOUCHER_PAYMENT_PATH));

    return manualVoucherPayments;
  } catch (error) {
    toastr.error('Error', error.message);

    return null;
  } finally {
    dispatch(flagManualVoucherPaymentActivity(false));
  }
};

const putManualVoucherPayment =
  async (
    manualVoucherPaymentId,
    {
      bankAccountId,
      manualVoucherId,
      businessId,
      customerId,
      operationNumber,
      amount,
      depositDate,
    },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagManualVoucherPaymentActivity(true));

      const payload = {
        id: manualVoucherPaymentId,
        bankAccountId,
        manualVoucherId,
        businessId,
        customerId,
        operationNumber,
        amount,
        depositDate,
      };

      const url = `${MANUAL_VOUCHER_PAYMENT_ENDPOINT}/${manualVoucherPaymentId}`;

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const manualVoucherPayment = await response.json();

      toastr.success(
        'Éxito',
        manualVoucherPayment.approved
          ? 'Pago actualizado y aprobado automáticamente'
          : 'Pago actualizado correctamente',
      );

      dispatch({
        type: GET_MANUAL_VOUCHER_PAYMENT,
        payload: manualVoucherPayment,
      });

      dispatch(
        push(`${MANUAL_VOUCHER_PAYMENT_PATH}/${manualVoucherPayment.id}`),
      );

      return manualVoucherPayment;
    } catch (error) {
      toastr.error('Error', error.message);

      return null;
    } finally {
      dispatch(flagManualVoucherPaymentActivity(false));
    }
  };

const deleteManualVoucherPayment =
  async ({ manualVoucherPaymentId }) =>
  async (dispatch) => {
    try {
      dispatch(flagManualVoucherPaymentActivity(true));

      const url = `${MANUAL_VOUCHER_PAYMENT_ENDPOINT}/${manualVoucherPaymentId}`;

      const response = await fetch(url, DEFAULT_DELETE_CONFIG);

      await isErrorResponse(response, null, dispatch);

      toastr.success('Éxito', 'Pago eliminado correctamente');

      dispatch(push(MANUAL_VOUCHER_PAYMENT_PATH));

      return true;
    } catch (error) {
      toastr.error('Error', error.message);

      return false;
    } finally {
      dispatch(flagManualVoucherPaymentActivity(false));
    }
  };

const approveManualVoucherPayment =
  async ({ manualVoucherPaymentId, tableFilters = {} }) =>
  async (dispatch) => {
    try {
      dispatch(flagManualVoucherPaymentActivity(true));

      const url = `${MANUAL_VOUCHER_PAYMENT_ENDPOINT}/${manualVoucherPaymentId}/approve`;

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
      });

      await isErrorResponse(response, null, dispatch);

      const manualVoucherPayment = await response.json();

      toastr.success('Éxito', 'Pago aprobado correctamente');

      dispatch({
        type: GET_MANUAL_VOUCHER_PAYMENT,
        payload: manualVoucherPayment,
      });

      await dispatch(getManualVoucherPayments(tableFilters));

      return manualVoucherPayment;
    } catch (error) {
      toastr.error('Error', error.message);

      return null;
    } finally {
      dispatch(flagManualVoucherPaymentActivity(false));
    }
  };

const flagManualVoucherPaymentReconciliation = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_MANUAL_VOUCHER_PAYMENT_RECONCILIATION,
    payload: flag,
  });

const getManualVoucherPaymentReconciliation =
  async ({ manualVoucherId }) =>
  async (dispatch) => {
    try {
      dispatch(flagManualVoucherPaymentReconciliation(true));

      const url =
        generateManualVoucherPaymentReconciliationEndpoint(manualVoucherId);

      const response = await fetch(url, DEFAULT_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const reconciliation = await response.json();

      dispatch({
        type: GET_MANUAL_VOUCHER_PAYMENT_RECONCILIATION,
        payload: reconciliation,
      });

      return reconciliation;
    } catch (error) {
      toastr.error('Error', error.message);

      return null;
    } finally {
      dispatch(flagManualVoucherPaymentReconciliation(false));
    }
  };

const reconcileManualVoucherPayments =
  async ({ manualVoucherId, manualVoucherPaymentIds }) =>
  async (dispatch) => {
    try {
      dispatch(flagManualVoucherPaymentReconciliation(true));

      const payload = {
        manualVoucherId,
        manualVoucherPaymentIds,
      };

      const response = await fetch(
        MANUAL_VOUCHER_PAYMENT_RECONCILIATION_ENDPOINT,
        {
          ...DEFAULT_POST_CONFIG,
          body: JSON.stringify(payload),
        },
      );

      await isErrorResponse(response, null, dispatch);

      const reconciliation = await response.json();

      toastr.success('Éxito', 'Pagos conciliados correctamente');

      dispatch({
        type: GET_MANUAL_VOUCHER_PAYMENT_RECONCILIATION,
        payload: reconciliation,
      });

      return reconciliation;
    } catch (error) {
      toastr.error('Error', error.message);

      return null;
    } finally {
      dispatch(flagManualVoucherPaymentReconciliation(false));
    }
  };

const clearManualVoucherPaymentReconciliation = () => (dispatch) =>
  dispatch({
    type: CLEAR_MANUAL_VOUCHER_PAYMENT_RECONCILIATION,
  });

export {
  getManualVoucherPayments,
  clearManualVoucherPayments,
  getManualVoucherPayment,
  clearManualVoucherPayment,
  postManualVoucherPayment,
  putManualVoucherPayment,
  deleteManualVoucherPayment,
  flagGettingManualVoucherPayments,
  flagManualVoucherPaymentActivity,
  approveManualVoucherPayment,
  getManualVoucherPaymentReconciliation,
  reconcileManualVoucherPayments,
  clearManualVoucherPaymentReconciliation,
  flagManualVoucherPaymentReconciliation,
};
